import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowUpRight, X, Star, MapPin, ShoppingBag, Layers, Smartphone,
  Search, Zap, Server, ChevronLeft, ChevronRight,
} from "lucide-react";
import { JHALA_GLOBAL_CSS, TopStrip, Header, Footer, Reveal, TagChip } from "components/jhala/Layout.js";

import pinStoreFrame from "images/companyWorkImage/pinstore/frame.png";
import pinWork1 from "images/companyWorkImage/pinstore/work1.png";
import pinWork2 from "images/companyWorkImage/pinstore/work2.png";
import pinWork3 from "images/companyWorkImage/pinstore/work3.png";
import pinWork4 from "images/companyWorkImage/pinstore/work4.png";

import truFrame from "images/companyWorkImage/truHair&Skin/frame.png";
import truWork1 from "images/companyWorkImage/truHair&Skin/work1.png";
import truWork2 from "images/companyWorkImage/truHair&Skin/work2.png";
import truWork3 from "images/companyWorkImage/truHair&Skin/work3.png";
import truWork4 from "images/companyWorkImage/truHair&Skin/work4.png";

import natureFrame from "images/companyWorkImage/natureMasons/frame.png";
import natureWork1 from "images/companyWorkImage/natureMasons/work1.png";
import natureWork2 from "images/companyWorkImage/natureMasons/work2.png";
import natureWork3 from "images/companyWorkImage/natureMasons/work3.png";
import natureWork4 from "images/companyWorkImage/natureMasons/work4.png";

import muffFrame from "images/companyWorkImage/mufftech/frame.png";
import muffWork1 from "images/companyWorkImage/mufftech/work1.png";
import muffWork2 from "images/companyWorkImage/mufftech/work2.png";
import muffWork3 from "images/companyWorkImage/mufftech/work3.png";
import muffWork4 from "images/companyWorkImage/mufftech/work4.png";

import maiaFrame from "images/companyWorkImage/maiaHerbs/frame.png";
import maiaWork1 from "images/companyWorkImage/maiaHerbs/work1.png";
import maiaWork2 from "images/companyWorkImage/maiaHerbs/work2.png";
import maiaWork3 from "images/companyWorkImage/maiaHerbs/work3.png";
import maiaWork4 from "images/companyWorkImage/maiaHerbs/work4.png";

import buildkeyFrame from "images/companyWorkImage/buildkey/frame.png";
import buildkeyWork1 from "images/companyWorkImage/buildkey/work1.png";
import buildkeyWork2 from "images/companyWorkImage/buildkey/work2.png";
import buildkeyWork3 from "images/companyWorkImage/buildkey/work3.png";
import buildkeyWork4 from "images/companyWorkImage/buildkey/work4.png";

import manufactFrame from "images/companyWorkImage/manufact/frame.png";
import manufactWork1 from "images/companyWorkImage/manufact/work1.png";
import manufactWork2 from "images/companyWorkImage/manufact/work2.png";
import manufactWork3 from "images/companyWorkImage/manufact/work3.png";
import manufactWork4 from "images/companyWorkImage/manufact/work4.png";

import myDiaryFrame from "images/companyWorkImage/myDiary/frame.png";

const PROJECTS = [
  {
    category: "Shopify",
    hash: "a4f9c2",
    frame: pinStoreFrame,
    gallery: [pinWork1, pinWork2, pinWork3, pinWork4],
    title: "The Pins Store",
    tagline: "Customized Shopify storefront with product filtering, secure checkout, and payment gateway integration.",
    location: "India",
    method: "Remote",
    rating: "4.8",
    tech: "Shopify, Liquid, JavaScript, HTML, CSS, TailwindCSS",
    challenge: "The client needed a modern, high-performing Shopify storefront that could handle complex product filtering, provide a seamless checkout experience, and integrate multiple payment gateways while maintaining fast load times and mobile responsiveness.",
    solution: [
      "Advanced product filtering system with real-time search",
      "Secure checkout with multiple payment gateway integrations",
      "Mobile-responsive design optimized for conversions",
      "Analytics tracking and automated inventory synchronization",
      "SEO enhancements for better discoverability",
    ],
    results: [
      "50% faster page load times",
      "Processed 5K+ orders successfully",
      "99.9% uptime and reliability",
      "40% increase in mobile conversions",
    ],
  },
  {
    category: "Shopify",
    hash: "e21b7d",
    frame: truFrame,
    gallery: [truWork1, truWork2, truWork3, truWork4],
    title: "Tru Hair and Skin",
    tagline: "E-commerce Shopify store with subscriptions, discounts, and SEO optimization.",
    location: "India",
    method: "Remote",
    rating: "4.9",
    tech: "Shopify, React.js, Liquid, Material-UI, SEO tools",
    challenge: "The beauty brand required a sophisticated e-commerce platform that could handle subscription-based product flows, dynamic discount management, and provide an engaging shopping experience while maximizing SEO visibility and organic traffic.",
    solution: [
      "Subscription-based product flows with recurring billing",
      "Dynamic discount code system and custom product pages",
      "React components for interactive UI elements",
      "SEO optimization to boost organic traffic",
      "Automated order confirmations and secure payment integration",
    ],
    results: [
      "60% increase in subscription sign-ups",
      "Processed 8K+ orders with 99.8% success rate",
      "45% boost in organic traffic",
      "35% improvement in customer retention",
    ],
  },
  {
    category: "Shopify",
    hash: "9c3f10",
    frame: natureFrame,
    gallery: [natureWork1, natureWork2, natureWork3, natureWork4],
    title: "The Nature Masons",
    tagline: "Eco-friendly Shopify store with custom theme, advanced search, and shipping integration.",
    location: "India",
    method: "On Site",
    rating: "4.7",
    tech: "Shopify, Liquid, TailwindCSS, JavaScript, API Integrations",
    challenge: "An eco-friendly brand needed a custom Shopify store that reflected their sustainable values, with advanced search capabilities, seamless shipping integration, and analytics to track performance while maintaining a fast, responsive user experience.",
    solution: [
      "Custom theme design aligned with brand identity",
      "Advanced product search functionality with filters",
      "Shipping API integration for real-time rates",
      "Analytics dashboards for order tracking",
      "Optimized product catalog layouts and responsive design",
    ],
    results: [
      "55% faster page load times",
      "Processed 12K+ orders successfully",
      "99.9% uptime and system reliability",
      "50% increase in search-to-purchase conversion",
    ],
  },
  {
    category: "Shopify",
    hash: "6d0aa5",
    frame: muffFrame,
    gallery: [muffWork1, muffWork2, muffWork3, muffWork4],
    title: "Mufftech",
    tagline: "Scalable Shopify solution with B2B/B2C workflows and ERP integration.",
    location: "India",
    method: "Remote",
    rating: "4.5",
    tech: "Shopify, Node.js (ERP integration), Liquid, Bootstrap",
    challenge: "A growing business needed a scalable Shopify solution that could handle both B2B and B2C workflows, with custom pricing logic, wholesale management, and seamless ERP integration for inventory synchronization across multiple channels.",
    solution: [
      "Dual B2B/B2C sales workflows with custom pricing logic",
      "Wholesale management features and role-based reseller access",
      "ERP/inventory system integration for stock synchronization",
      "Automated checkout flows and reporting modules",
      "Streamlined order processing and fulfillment",
    ],
    results: [
      "Processed 15K+ orders across B2B and B2C channels",
      "99.8% inventory accuracy with ERP integration",
      "40% reduction in order processing time",
      "Scaled to handle 3x order volume",
    ],
  },
  {
    category: "Shopify",
    hash: "7f21ae",
    frame: maiaFrame,
    gallery: [maiaWork1, maiaWork2, maiaWork3, maiaWork4],
    title: "Maia Herbs",
    tagline: "Herbal Shopify store with product bundling, subscriptions, and email automation.",
    location: "India",
    method: "Remote",
    rating: "4.5",
    tech: "Shopify, Liquid, TailwindCSS, Klaviyo (Email Automation)",
    challenge: "An Ayurvedic products brand needed a clean, nature-inspired storefront with product bundling capabilities, subscription management, and email marketing automation to improve customer retention and boost repeat purchases.",
    solution: [
      "Nature-inspired custom theme design",
      "Product bundling and categorization for herbal products",
      "Subscription-based checkout flows with recurring billing",
      "Klaviyo email marketing automation integration",
      "Custom discount logic and intuitive buying journey",
    ],
    results: [
      "70% increase in subscription sign-ups",
      "Processed 6K+ orders with 99.9% success rate",
      "45% improvement in repeat purchase rate",
      "60% boost in email marketing engagement",
    ],
  },
  {
    category: "Web",
    hash: "b81c4e",
    frame: buildkeyFrame,
    gallery: [buildkeyWork1, buildkeyWork2, buildkeyWork3, buildkeyWork4],
    title: "Buildkey",
    tagline: "High-performance web application built with Next.js and Node.js, designed for a seamless and scalable user experience.",
    location: "Australia",
    method: "Remote",
    rating: "4.9",
    tech: "Next.js, Node.js, MongoDB",
    overview: "Developed entirely from scratch to the client's requirement of a secure, scalable web application hosted on Vercel. We used Next.js for the frontend for its performance, SEO advantages, and seamless Vercel integration, and Node.js for a fast, scalable backend. The architecture supports secure CRUD operations with MongoDB for flexible data handling, Stripe for secure payment processing, and role-based authentication with full protection against common security threats.",
  },
  {
    category: "Web",
    hash: "3d90f6",
    frame: manufactFrame,
    gallery: [manufactWork1, manufactWork2, manufactWork3, manufactWork4],
    title: "Manufact",
    tagline: "Modern, fully responsive web application built with Next.js, React, and MongoDB.",
    location: "Australia",
    method: "Remote",
    rating: "4.5",
    tech: "Next.js, Node.js, MongoDB, Blockchain",
    overview: "Manufact tracks the status of products using secure seals integrated with blockchain technology. Each seal contains encrypted information, ensuring user data remains completely private — even internal company teams cannot access it, adding a powerful layer of trust and security. Our developers and QA testers collaborated closely on frontend responsiveness, backend logic, and blockchain integration to deliver a secure, user-friendly, and innovative platform.",
  },
  {
    category: "Web",
    hash: "c56b28",
    frame: myDiaryFrame,
    gallery: [],
    title: "MyDiary",
    tagline: "User-friendly web application for tracking daily activities, built with React, PHP, and MySQL.",
    location: "MP, India",
    method: "On-Site",
    rating: "4.7",
    tech: "PHP, MySQL, React.js, CodeIgniter",
    overview: "Built entirely from scratch as an in-house project, completed in four months. The frontend uses React.js for a fast, responsive interface; the backend runs on PHP with the CodeIgniter framework for lightweight performance and security, with MySQL for data storage. MyDiary helps individual users track daily activities, manage expenses, and stay organized — its standout feature is an interactive calendar that centralizes tasks, events, and financial entries across daily and monthly views.",
  },
];

const CAPABILITIES = [
  {
    key: "shopify",
    label: "Shopify",
    icon: ShoppingBag,
    items: [
      { title: "Online Store Setup", subtitle: "Launch your store with ease in minutes", desc: "Comprehensive Shopify store setup — product import, payment setup, domain configuration, and SEO essentials to launch quickly." },
      { title: "Store Migration", subtitle: "Seamlessly move from any platform to Shopify", desc: "Smooth transition from WooCommerce, Magento, Wix, or BigCommerce — preserving data, design structure, SEO, and integrations." },
      { title: "Store Redesign", subtitle: "Refresh your brand with a modern look", desc: "A fresh, clean, modern storefront that better reflects your brand, improves navigation, and lifts conversions." },
      { title: "App Integration", subtitle: "Connect powerful tools to grow your store", desc: "CRM, email marketing, inventory management, analytics, and more — selected and configured for your business." },
      { title: "Theme Customization", subtitle: "Design that matches your brand", desc: "Fine-tuned layout, typography, and brand colors, from minor tweaks to full custom theme builds." },
    ],
  },
  {
    key: "web",
    label: "Web Development",
    icon: Layers,
    items: [
      { title: "Full-Cycle Development", subtitle: "Frontend, backend, integrations & deployment", desc: "Scalable frontends, robust backends, hosting, and integrations handled end to end — concept through launch." },
      { title: "Web Hosting", subtitle: "Domain, SSL, backups & support included", desc: "Secure, reliable hosting with full domain setup, SSL configuration, automated backups, and dedicated support." },
      { title: "Maintenance & Support", subtitle: "Regular updates, backups, and troubleshooting", desc: "Ongoing updates, security patches, and monitoring so your site stays fast, secure, and online." },
    ],
  },
  {
    key: "app",
    label: "App Development",
    icon: Smartphone,
    items: [
      { title: "Custom Mobile & Web Apps", subtitle: "From concept to deployment", desc: "End-to-end development for startups and enterprises using React, Node.js, Flutter, and Next.js — MVP to full-scale platform." },
      { title: "Maintenance & Enhancements", subtitle: "Reliable post-launch support", desc: "Uptime monitoring, security patches, bug fixes, and regular feature upgrades with flexible maintenance packages." },
    ],
  },
  {
    key: "seo",
    label: "SEO Optimization",
    icon: Search,
    items: [
      { title: "On-Page SEO", subtitle: "Meta tags, structure, and content optimization", desc: "Unique title tags, strategic heading structure, natural keyword integration, and internal linking that search engines and users both follow." },
      { title: "Technical SEO", subtitle: "Site architecture and performance", desc: "Site speed, mobile responsiveness, HTTPS, structured data, canonical URLs, sitemaps, and crawlability fixes." },
      { title: "Content SEO", subtitle: "High-quality, keyword-focused content", desc: "Original, well-structured content built around search intent, readability, and topical authority." },
      { title: "Off-Page SEO", subtitle: "Authority and trust through external signals", desc: "High-quality backlinks, brand mentions, influencer outreach, and social engagement that build domain trust." },
    ],
  },
  {
    key: "speed",
    label: "Page Speed",
    icon: Zap,
    items: [
      { title: "Asset Optimization", subtitle: "Compress, convert, and lazy-load", desc: "Image and media compression, modern formats like WebP, lazy loading, and CDN delivery for faster Core Web Vitals scores." },
    ],
  },
  {
    key: "backend",
    label: "Backend & DevOps",
    icon: Server,
    items: [
      { title: "Infrastructure Automation", subtitle: "Optimizing CI/CD pipelines", desc: "Streamlined deployment pipelines with Jenkins and Docker, scalable automation via Terraform and Ansible." },
      { title: "Server Management", subtitle: "24x7 uptime guarantee", desc: "Continuous monitoring, proactive patching, and real-time alerts using Nagios, Prometheus, and Grafana." },
      { title: "Tech Consulting", subtitle: "Monolith to microservices", desc: "Architecture revamps — container orchestration, modular service design, and event-driven communication." },
    ],
  },
];

function AutoCycleImage({ images, alt }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % images.length);
    }, 2200);
    return () => clearInterval(id);
  }, [images.length]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {images.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={alt}
          style={{
            position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain",
            opacity: i === index ? 1 : 0, transition: "opacity 0.6s ease",
          }}
        />
      ))}
    </div>
  );
}

function ProjectModal({ project, onClose }) {
  const [activeImg, setActiveImg] = useState(0);
  const images = project ? [project.frame, ...project.gallery] : [];

  useEffect(() => {
    if (images.length <= 1) return;
    const id = setInterval(() => {
      setActiveImg((i) => (i + 1) % images.length);
    }, 2500);
    return () => clearInterval(id);
  }, [images.length]);

  if (!project) return null;

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(10,15,30,0.75)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "#F6F4EE", borderRadius: 10, maxWidth: 780, width: "100%", maxHeight: "90vh", overflowY: "auto", position: "relative" }}
      >
        <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, background: "#0A0F1E", border: "none", borderRadius: "50%", width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", zIndex: 2 }}>
          <X size={18} color="#F6F4EE" />
        </button>

        <div style={{ position: "relative", background: "#0A0F1E", height: 360 }}>
          {images.map((src, i) => (
            <img
              key={src}
              src={src}
              alt={project.title}
              style={{
                position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain",
                opacity: i === activeImg ? 1 : 0, transition: "opacity 0.6s ease",
              }}
            />
          ))}
          {images.length > 1 && (
            <>
              <button onClick={() => setActiveImg((activeImg - 1 + images.length) % images.length)} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", background: "rgba(10,15,30,0.6)", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ChevronLeft size={18} color="#F6F4EE" />
              </button>
              <button onClick={() => setActiveImg((activeImg + 1) % images.length)} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "rgba(10,15,30,0.6)", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <ChevronRight size={18} color="#F6F4EE" />
              </button>
            </>
          )}
        </div>

        <div style={{ padding: "28px 30px 34px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
            <TagChip tone={project.category === "Shopify" ? "signal" : "circuit"}>#{project.hash}</TagChip>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, color: "#4A5468" }}>
              <Star size={13} fill="#FF5C39" color="#FF5C39" /> {project.rating}
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 13, color: "#4A5468" }}>
              <MapPin size={13} /> {project.location}
            </span>
          </div>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 26, fontWeight: 700, margin: "0 0 10px" }}>{project.title}</h2>
          <p style={{ color: "#4A5468", fontSize: 14.5, lineHeight: 1.7, marginBottom: 20 }}>{project.tagline}</p>

          {project.challenge && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 600, margin: "0 0 8px" }}>Challenge</h3>
              <p style={{ color: "#4A5468", fontSize: 14, lineHeight: 1.7, margin: 0 }}>{project.challenge}</p>
            </div>
          )}
          {project.solution && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 600, margin: "0 0 8px" }}>Solution</h3>
              <ul style={{ margin: 0, padding: "0 0 0 20px", display: "flex", flexDirection: "column", gap: 6 }}>
                {project.solution.map((s) => <li key={s} style={{ color: "#4A5468", fontSize: 14, lineHeight: 1.6 }}>{s}</li>)}
              </ul>
            </div>
          )}
          {project.results && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 600, margin: "0 0 8px" }}>Results</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {project.results.map((r) => <TagChip key={r} tone="circuit">{r}</TagChip>)}
              </div>
            </div>
          )}
          {project.overview && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 600, margin: "0 0 8px" }}>Project Details</h3>
              <p style={{ color: "#4A5468", fontSize: 14, lineHeight: 1.7, margin: 0 }}>{project.overview}</p>
            </div>
          )}

          <div>
            <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15, fontWeight: 600, margin: "0 0 8px" }}>Technologies</h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {project.tech.split(", ").map((t) => <TagChip key={t}>{t}</TagChip>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function WorkPage() {
  const [selected, setSelected] = useState(null);
  const [activeCategory, setActiveCategory] = useState(CAPABILITIES[0].key);
  const activeCapability = CAPABILITIES.find((c) => c.key === activeCategory);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const hash = searchParams.get("project");
    if (hash) {
      const match = PROJECTS.find((p) => p.hash === hash);
      if (match) setSelected(match);
    }
  }, [searchParams]);

  const closeModal = () => {
    setSelected(null);
    if (searchParams.get("project")) {
      const next = new URLSearchParams(searchParams);
      next.delete("project");
      setSearchParams(next, { replace: true });
    }
  };

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: "#F6F4EE", color: "#0A0F1E" }}>
      <style>{JHALA_GLOBAL_CSS}</style>
      <TopStrip />
      <Header active="Work" />

      {/* HERO */}
      <section style={{ background: "#0A0F1E", color: "#F6F4EE", padding: "64px 24px 56px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(#23304A 1px, transparent 1px), linear-gradient(90deg, #23304A 1px, transparent 1px)", backgroundSize: "48px 48px", opacity: 0.2, maskImage: "radial-gradient(ellipse at 70% 30%, black, transparent 70%)" }} />
        <div style={{ maxWidth: 1180, margin: "0 auto", position: "relative" }}>
          <Reveal>
            <TagChip>$ jhalatech --work --list</TagChip>
            <h1 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "clamp(30px, 4.4vw, 46px)", lineHeight: 1.1, margin: "18px 0 14px" }}>
              Work that speaks for itself.
            </h1>
            <p style={{ fontSize: 16, color: "#B8BFCF", maxWidth: 580, lineHeight: 1.65 }}>
              Real Shopify stores and web platforms we've shipped, plus the full range of what we deliver —
              from store setup to SEO to backend infrastructure.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SELECTED WORK */}
      <section style={{ padding: "80px 24px 40px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <TagChip tone="signal">#SELECTED-WORK</TagChip>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#8B93A7" }}>01 / client projects</span>
            </div>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(24px, 3vw, 34px)", fontWeight: 700, margin: "0 0 14px" }}>
              Stores & platforms we've built
            </h2>
            <p style={{ color: "#4A5468", fontSize: 15.5, maxWidth: 620, lineHeight: 1.7, marginBottom: 40 }}>
              A mix of Shopify storefronts and custom web platforms — click any project for the challenge,
              solution, and results.
            </p>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
            {PROJECTS.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 0.06}>
                <button
                  onClick={() => setSelected(p)}
                  className="svc-card"
                  style={{ display: "block", width: "100%", textAlign: "left", border: "1px solid #DCD8CC", borderRadius: 8, background: "#FFFFFF", overflow: "hidden", cursor: "pointer", padding: 0, height: "100%" }}
                >
                  <div style={{ background: "#0A0F1E", height: 170, position: "relative", overflow: "hidden" }}>
                    <AutoCycleImage images={[p.frame, ...p.gallery]} alt={p.title} />
                  </div>
                  <div style={{ padding: "20px 20px 22px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                      <TagChip tone={p.category === "Shopify" ? "signal" : "circuit"}>#{p.hash}</TagChip>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12.5, color: "#4A5468" }}>
                        <Star size={12} fill="#FF5C39" color="#FF5C39" /> {p.rating}
                      </span>
                    </div>
                    <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 16.5, fontWeight: 600, margin: "0 0 6px" }}>{p.title}</h3>
                    <p style={{ color: "#6B7280", fontSize: 13, lineHeight: 1.55, margin: "0 0 14px" }}>{p.tagline}</p>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, color: "#8B93A7" }}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}><MapPin size={12} /> {p.location}</span>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 3, color: "#0A0F1E", fontWeight: 600 }}>View Details <ArrowUpRight size={13} /></span>
                    </div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section style={{ padding: "70px 24px 90px", background: "#F6F4EE" }}>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <Reveal>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#8B93A7" }}>02 / everything we deliver</span>
            </div>
            <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(24px, 3vw, 34px)", fontWeight: 700, margin: "0 0 30px" }}>
              Beyond the case studies
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 32 }}>
              {CAPABILITIES.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setActiveCategory(c.key)}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 16px", borderRadius: 999,
                    border: activeCategory === c.key ? "1px solid #0A0F1E" : "1px solid #DCD8CC",
                    background: activeCategory === c.key ? "#0A0F1E" : "#FFFFFF",
                    color: activeCategory === c.key ? "#F6F4EE" : "#4A5468",
                    fontWeight: 600, fontSize: 13.5, cursor: "pointer", transition: "all 0.2s ease",
                  }}
                >
                  <c.icon size={15} />
                  {c.label}
                </button>
              ))}
            </div>
          </Reveal>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 18 }}>
            {activeCapability.items.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.05}>
                <div className="svc-card" style={{ border: "1px solid #DCD8CC", borderRadius: 8, padding: "22px 20px", background: "#FFFFFF", height: "100%" }}>
                  <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: 15.5, fontWeight: 600, margin: "0 0 4px" }}>{item.title}</h3>
                  <p style={{ color: "#FF5C39", fontSize: 12.5, fontWeight: 500, margin: "0 0 10px" }}>{item.subtitle}</p>
                  <p style={{ color: "#6B7280", fontSize: 13.5, lineHeight: 1.6, margin: 0 }}>{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "70px 24px", background: "#0A0F1E", color: "#F6F4EE", textAlign: "center" }}>
        <Reveal>
          <h2 style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "clamp(22px, 3vw, 30px)", fontWeight: 700, margin: "0 0 14px" }}>
            Want results like these?
          </h2>
          <p style={{ color: "#B8BFCF", fontSize: 15, marginBottom: 26 }}>
            Tell us about your project and we'll show you how we'd approach it.
          </p>
          <Link to="/contact-us" className="cta-btn" style={{ background: "#4CE0B3", color: "#0A0F1E", border: "none", padding: "13px 24px", borderRadius: 4, fontWeight: 600, fontSize: 14, cursor: "pointer", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 8 }}>
            Get In Touch <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </section>

      <Footer />

      {selected && <ProjectModal key={selected.hash} project={selected} onClose={closeModal} />}
    </div>
  );
}
